import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faBolt, faGear } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components/Icon/Icon";
import { Spinner } from "@/components/Spinner/Spinner";
import { classNames } from "@/utils/classNames";

import { buildSnippetToken } from "../utils/snippetTokens";
import { SnippetRow } from "../utils/snippetRows";

interface SnippetSectionRowProps {
	row: SnippetRow;
	isPending: boolean;
	onInsert: (row: SnippetRow) => void;
	onConfigure: (row: SnippetRow) => void;
}

/**
 * One row of the AI snippets section: icon, name, status badge
 * ("In message" / "Insert", plus "Template" for company-wide templates) and a
 * gear that opens the snippet configuration. Clicking the row inserts the
 * snippet; list-column rows can also be dragged into the editor.
 */
export const SnippetSectionRow: FunctionComponent<SnippetSectionRowProps> = ({
	row,
	isPending,
	onInsert,
	onConfigure,
}) => {
	const { t } = useTranslation();

	const isListColumn = row.kind === "listColumn";
	const isInserted = isListColumn && row.isInserted;

	return (
		<div
			aria-busy={isPending}
			className={classNames(
				"group w-full flex items-center gap-2 px-2.5 py-2 rounded-md border border-transparent text-left transition-colors",
				isPending
					? "opacity-60 pointer-events-none"
					: "cursor-pointer hover:bg-background-light hover:border-border-light"
			)}
			data-column-config={isListColumn ? JSON.stringify(row.column.config) : undefined}
			data-column-id={isListColumn ? row.columnId : undefined}
			data-column-name={row.name}
			draggable={isListColumn}
			role="button"
			onClick={() => onInsert(row)}
			onDragStart={
				isListColumn
					? (e: React.DragEvent<HTMLDivElement>) =>
							e.dataTransfer?.setData("text", buildSnippetToken(row.columnId, row.name))
					: undefined
			}
		>
			<span className="w-[22px] h-[22px] flex-shrink-0 rounded bg-blue-action-200 text-blue-action flex items-center justify-center">
				<Icon className="w-3 h-3" icon={faBolt} />
			</span>

			<span className="flex-shrink min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-[12.5px] font-medium text-text">
				{row.name}
			</span>

			<span className="flex-1 min-w-2" />

			{isPending ? (
				<Spinner color="#4D66F5" />
			) : (
				<>
					{row.kind === "template" && (
						<span className="flex-shrink-0 text-xxs font-medium px-1.5 py-0.5 rounded bg-blue-action-200 text-blue-action">
							{t("aiSnippet.section.template")}
						</span>
					)}
					<span
						className={classNames(
							"flex-shrink-0 text-xxs font-medium px-1.5 py-0.5 rounded",
							isInserted
								? "bg-status-success-light text-status-success"
								: "bg-background-light text-text-meta"
						)}
					>
						{isInserted ? t("aiSnippet.section.inMessage") : t("aiSnippet.section.insert")}
					</span>
				</>
			)}

			<button
				aria-label={t("aiSnippet.section.configure")}
				className="w-6 h-6 flex-shrink-0 flex items-center justify-center rounded border-0 bg-transparent cursor-pointer text-text-meta hover:bg-border-light hover:text-text"
				title={t("aiSnippet.section.configure")}
				type="button"
				onClick={(e) => {
					e.stopPropagation();
					onConfigure(row);
				}}
			>
				<Icon className="w-3.5 h-3.5" icon={faGear} />
			</button>
		</div>
	);
};
